import {Collapse, IconButton, Stack, Typography} from '@mui/material';
import React, {useState} from 'react';
import {NciDict, NciGroupTree} from './types';
import {ReactComponent as ListOutlined} from '@assets/icons/ListOutlined.svg';
import {ExpandLess, ExpandMore} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';

type NciTreeProps = {
    tree: NciGroupTree;
    selectedDict: NciDict | null;
    setSelectedDict: (dict: NciDict) => void;
};

export const NciTree = ({tree, selectedDict, setSelectedDict}: NciTreeProps) => {
    const {t} = useTranslation('administration');
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

    const toggleGroup = (groupName: string) => {
        setExpandedGroups((prev) => ({
            ...prev,
            [groupName]: !prev[groupName],
        }));
    };

    return (
        <Stack
            direction='column'
            width={360}
            minWidth={360}
            pr={1}
            ml={3}
            sx={{
                height: 'calc(100vh - 128px)',
                overflowY: 'auto',
                scrollbarGutter: 'stable',
                flexShrink: 0,
            }}
        >
            <Typography variant='h6'>{t('nciGroup')}</Typography>
            {tree.map((group) => {
                const isExpanded = expandedGroups[group.groupName] || false;

                return (
                    <Stack key={group.groupName}>
                        <Stack
                            direction='row'
                            justifyContent='space-between'
                            alignItems='center'
                            onClick={() => toggleGroup(group.groupName)}
                            sx={{padding: '4px 0px', cursor: 'pointer'}}
                        >
                            <Stack
                                direction='row'
                                gap={1}
                                alignItems='center'
                            >
                                <ListOutlined />
                                <Typography>{group.groupName}</Typography>
                            </Stack>
                            <IconButton size='small'>
                                {isExpanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </Stack>

                        <Collapse
                            in={isExpanded}
                            timeout='auto'
                        >
                            {group.dicts.map((dict) => {
                                const isSelected = selectedDict?.dictName === dict.dictName;

                                return (
                                    <Stack
                                        key={dict.dictName}
                                        sx={{
                                            paddingLeft: 4,
                                            cursor: 'pointer',
                                            background: isSelected
                                                ? 'linear-gradient(90deg, rgba(2, 86, 127, 0) 0%, rgba(3, 155, 229, 0.08) 8.68%, rgba(3, 155, 229, 0.08) 43%, rgba(2, 86, 127, 0) 100%)'
                                                : '',
                                            '&:hover': {color: 'primary.main'},
                                            paddingY: 1,
                                        }}
                                        onClick={() => setSelectedDict(dict)}
                                    >
                                        <Typography color={isSelected ? 'primary' : ''}>
                                            {dict.dictName}
                                        </Typography>
                                    </Stack>
                                );
                            })}
                        </Collapse>
                    </Stack>
                );
            })}
        </Stack>
    );
};
